import { createResource, postgRESTApiUrl } from "./api";

const DEFAULT_LIMIT = 30;

/**
 * Get feed items for a specific profile using cursor-based pagination.
 *
 * @param {number|string} profileId - The profile ID to fetch feed items for
 * @param {Object} options - Optional parameters
 * @param {number} options.limit - Maximum number of items to return (default: 30, clamped 1-100 server-side)
 * @param {string|null} options.cursorSortTimestamp - sort_timestamp of last item from previous page (null for first page)
 * @param {string|null} options.cursorId - id of last item from previous page (null for first page)
 * @returns {Promise<{data: Array} | {error: Object}>}
 */
const getByProfileId = async (profileId, options = {}) => {
  const { limit = DEFAULT_LIMIT, cursorSortTimestamp = null, cursorId = null } = options;

  // Cursor fields must both be present or both absent
  const hasCursorTimestamp = cursorSortTimestamp != null;
  const hasCursorId = cursorId != null;
  if (hasCursorTimestamp !== hasCursorId) {
    return {
      error: {
        status: 400,
        statusText: "Bad Request",
        body: { message: "Both cursorSortTimestamp and cursorId are required together" },
      },
    };
  }

  const body = {
    p_profile_id: Number(profileId),
    p_limit: limit,
    p_cursor_sort_timestamp: cursorSortTimestamp,
    p_cursor_id: cursorId,
  };

  const url = `${postgRESTApiUrl()}/rest/v1/rpc/get_profile_feed_items`;
  return createResource(url, body);
};

/**
 * Check whether a profile has any personalized feed items.
 *
 * @param {number|string} profileId
 * @returns {Promise<{data: boolean} | {error: Object}>}
 */
const hasPersonalizedItems = async (profileId) => {
  const body = { p_profile_id: Number(profileId) };
  const url = `${postgRESTApiUrl()}/rest/v1/rpc/has_personalized_feed_items`;
  return createResource(url, body);
};

export default { getByProfileId, hasPersonalizedItems, DEFAULT_LIMIT };
